<template>
  <article class="product-card group">
    <div class="relative">
      <!-- Badge overlay -->
      <div v-if="product.tags.length > 0" class="absolute top-3 left-3 z-10 flex gap-2">
        <ProductTag v-for="tag in product.tags" :key="tag.name" :tag="tag" />
      </div>

      <!-- Wishlist button -->
      <button
        v-if="enableWishlist"
        @click.stop="toggleWishlist"
        class="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all shadow-sm"
        :class="{ 'text-red-500 bg-red-50 dark:bg-red-900/30': wishlistItem, 'text-gray-600 dark:text-gray-400': !wishlistItem }"
        aria-label="Add to wishlist"
      >
        <Icon :name="wishlistItem ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'" class="w-5 h-5" />
      </button>

      <!-- Product image with hover effects -->
      <NuxtLink :to="$localePath({ name: 'product-slug', params: { slug: product.slug } })" class="block">
        <div class="relative aspect-square bg-gray-100 dark:bg-gray-800 overflow-hidden rounded-lg">

          <img
            v-if="product.images.length > 0"
            :src="product.images[0]!.url"
            :alt="product.name"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-100"
            loading="lazy"
          />
          <img 
            v-else
            src="/img/placeholder.jpg"
            alt="Placeholder"
            class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-100"
            loading="lazy"
          />
        </div>
      </NuxtLink>

      <!-- Quick add button on hover -->
      <div class="absolute inset-x-0 bottom-0 opacity-0 group-hover:opacity-100 transition-opacity z-10 p-3">
        <UButton v-if="product.stock && product.variants.length == 1" block @click.stop="addToCart(product.variants[0]!)" size="lg" variant="outline" color="neutral" class="shadow-lg">
          {{ $t('Add to cart') }}
        </UButton>
        <UButton v-else block :to="$localePath({ name: 'product-slug', params: { slug: product.slug } })" size="lg" variant="outline" color="neutral" class="shadow-lg">
          {{ $t('Add to cart') }}
        </UButton>
      </div>
    </div>

    <!-- Product info -->
    <div class="py-4 pt-3 space-y-2">
      <!-- Title -->
      <NuxtLink :to="$localePath({ name: 'product-slug', params: { slug: product.slug } })" class="block">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 group-hover:underline hover:text-primary transition-colors mb-1">
          {{ product.name }}
        </h3>
      </NuxtLink>

      <!-- Rating -->
      <!-- <div v-if="product.rating" class="flex items-center gap-1">
        <Icon name="i-heroicons-star-solid" class="w-4 h-4 text-yellow-400" />
        <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ product.rating }}</span>
      </div> -->

      <!-- Price -->
      <div class="flex items-baseline gap-2">
        <PriceCompare :price="product.price" :oldPrice="product.regularPrice" class="text-base font-bold" />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ProductTag from '~/components/atoms/ProductTag.vue'
import { useCartStore } from '~/stores/useCartStore'
import { useWishlistStore } from '~/stores/useWishlistStore'
import type { ListProduct, Product, NewCartItem, ProductVariant } from '~/types'
import PriceCompare from './PriceCompare.vue'

const {
    product,
    enableWishlist = true
} = defineProps<{
    product: ListProduct | Product
    enableWishlist?: boolean
  }>();

const cart = useCartStore()
const wishlist = useWishlistStore()
const toast = useToast()
const { t } = useI18n()
const addToCartModal = useAddToCartModal()

const wishlistItem = computed(() => wishlist.isWishlisted(product.id))

function addToCart(variant : ProductVariant) {
  const cartItem : NewCartItem = {
    product,
    variant,
    quantity: 1
  }

  cart.addProduct({ productId: product.id, quantity: 1, variantId: variant.id })

  addToCartModal.show(cartItem)
}

function toggleWishlist() {
  try {
    if (wishlistItem.value) {
      wishlist.removeItem(wishlistItem.value.id)
    } else {
      wishlist.addProduct(product.id)
    }
  } catch (error) {
    console.error(error)

    toast.add({
      title: t('Error'),
      description: t('There was an error updating your wishlist.'),
      color: 'error'
    })
  }
}
</script>